/**
 * CI/CD Icons Registry
 *
 * Provides CI/CD and DevOps tool icons from devicons-react package.
 * Note: devicons-react uses `size` prop (not `width`/`height`).
 *
 * @see https://www.npmjs.com/package/devicons-react
 */

import {
  AnsibleOriginal,
  ArgocdOriginal,
  AzuredevopsOriginal,
  BambooOriginal,
  BitbucketOriginal,
  CircleciPlain,
  CloudflareOriginal,
  DockerOriginal,
  GithubactionsPlain,
  GithubcodespacesPlain,
  GithubOriginal,
  GitlabOriginal,
  HerokuOriginal,
  JenkinsOriginal,
  KubernetesPlain,
  NetlifyOriginal,
  PulumiOriginal,
  TerraformOriginal,
  TravisOriginal,
  VercelLine,
} from 'devicons-react';
import type { ComponentType, ReactNode } from 'react';

import { useTheme } from '@/app/ThemeContext';
import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { CICD_ICONS } from '@/sections/attacks/attackPaths/components/icons/types';

// ============================================================================
// Icon Component Type
// ============================================================================

type CicdIconComponent = ComponentType<{
  className?: string;
  size?: number;
  color?: string;
}>;

// ============================================================================
// Fallback Component
// ============================================================================

const FallbackIcon = ({ className }: { className?: string }) => (
  <span className={className}>?</span>
);

// ============================================================================
// Theme-Aware Icon Wrappers
// ============================================================================

/**
 * Wrapper for icons that support color prop - passes currentColor for theme adaptation.
 */
const createCurrentColorIcon = (
  IconComponent: CicdIconComponent
): CicdIconComponent => {
  return function ThemedIcon({
    size,
    className,
  }: {
    size?: number;
    className?: string;
  }) {
    return (
      <IconComponent size={size} className={className} color="currentColor" />
    );
  };
};

/**
 * Wrapper for icons that need CSS invert in dark mode.
 * Uses useTheme hook to react to theme changes.
 */
const createInvertIcon = (
  IconComponent: CicdIconComponent
): CicdIconComponent => {
  return function InvertedIcon({
    size,
    className,
  }: {
    size?: number;
    className?: string;
  }) {
    const { currentTheme } = useTheme();
    const isDark = currentTheme === 'dark';

    return (
      <span
        className={`inline-flex ${className ?? ''}`}
        style={{ filter: isDark ? 'invert(1)' : 'none' }}
      >
        <IconComponent size={size} />
      </span>
    );
  };
};

// ============================================================================
// Icon Component Map
// ============================================================================

/**
 * Explicit mapping from CICD_ICONS names to devicons-react components.
 * Uses type-safe keys derived from CICD_ICONS const array.
 * Icons that need dark mode handling are wrapped with appropriate handlers.
 */
const cicdIconComponents: Partial<
  Record<(typeof CICD_ICONS)[number], CicdIconComponent>
> = {
  // Theme-aware icons (dark mode handling)
  Github: createInvertIcon(GithubOriginal),
  GithubActions: createCurrentColorIcon(GithubactionsPlain),
  GithubCodespaces: createCurrentColorIcon(GithubcodespacesPlain),
  Vercel: createCurrentColorIcon(VercelLine),
  // Standard icons (work in both themes)
  Gitlab: GitlabOriginal,
  Jenkins: JenkinsOriginal,
  Circleci: CircleciPlain,
  Travis: TravisOriginal,
  AzureDevops: AzuredevopsOriginal,
  Argocd: ArgocdOriginal,
  Bamboo: BambooOriginal,
  Terraform: TerraformOriginal,
  Pulumi: PulumiOriginal,
  Ansible: AnsibleOriginal,
  Docker: DockerOriginal,
  Kubernetes: KubernetesPlain,
  Netlify: NetlifyOriginal,
  Heroku: HerokuOriginal,
  Cloudflare: CloudflareOriginal,
  Bitbucket: BitbucketOriginal,
};

/**
 * Human-readable labels for CI/CD icons.
 */
const cicdIconLabels: Partial<Record<(typeof CICD_ICONS)[number], string>> = {
  Github: 'GitHub',
  GithubActions: 'GitHub Actions',
  GithubCodespaces: 'GitHub Codespaces',
  Gitlab: 'GitLab',
  Jenkins: 'Jenkins',
  Circleci: 'CircleCI',
  Travis: 'Travis CI',
  AzureDevops: 'Azure DevOps',
  Argocd: 'Argo CD',
  Bamboo: 'Bamboo',
  Terraform: 'Terraform',
  Pulumi: 'Pulumi',
  Ansible: 'Ansible',
  Docker: 'Docker',
  Kubernetes: 'Kubernetes',
  Vercel: 'Vercel',
  Netlify: 'Netlify',
  Heroku: 'Heroku',
  Cloudflare: 'Cloudflare',
  Bitbucket: 'Bitbucket',
};

// ============================================================================
// CI/CD Renderer Implementation
// ============================================================================

/**
 * CI/CD icon renderer for the attack graph icon system.
 * Implements IconRenderer interface for registration with iconRenderers Map.
 */
const cicdRenderer: IconRenderer = {
  render: (
    name: string,
    { size = 24, className }: IconRenderProps
  ): ReactNode => {
    const IconComponent =
      cicdIconComponents[name as (typeof CICD_ICONS)[number]];
    if (!IconComponent) return null;
    // devicons-react uses `size` prop instead of width/height
    return <IconComponent className={className} size={size} />;
  },

  validate: (name: string): boolean => {
    return (CICD_ICONS as readonly string[]).includes(name);
  },

  getIcon: (name: string) => {
    const IconComponent =
      cicdIconComponents[name as (typeof CICD_ICONS)[number]];
    if (!IconComponent) return null;

    return function WrappedCicdIcon({
      size = 24,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return <IconComponent className={className} size={size} />;
    };
  },

  getAll: (): IconDefinition[] =>
    CICD_ICONS.map(name => {
      const IconComponent = cicdIconComponents[name];
      return {
        id: `cicd:${name}`,
        component: IconComponent ?? FallbackIcon,
        label: cicdIconLabels[name] ?? name,
        category: 'cicd',
      };
    }),
};

// ============================================================================
// Register Renderer
// ============================================================================

iconRenderers.set('cicd', cicdRenderer);

export { cicdRenderer };
